import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
  DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Award, ShieldCheck, ShieldX, Search, Loader2,
  ExternalLink, Ban, Calendar, User, BookOpen,
} from 'lucide-react';
import { toast } from 'sonner';

const AdminCertificates = () => {
  const { api } = useAuth();
  const [certificates, setCertificates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [revokeTarget, setRevokeTarget] = useState(null);
  const [revokeReason, setRevokeReason] = useState('');
  const [revoking, setRevoking] = useState(false);

  useEffect(() => { fetchCerts(); }, [statusFilter]);

  const fetchCerts = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter !== 'all') params.append('status', statusFilter);
      params.append('limit', '100');
      const res = await api.get(`/certificates/all?${params.toString()}`);
      setCertificates(res.data || []);
    } catch {
      toast.error('Failed to load certificates');
    } finally {
      setLoading(false);
    }
  };

  const handleRevoke = async () => {
    if (!revokeTarget || !revokeReason.trim()) return;
    setRevoking(true);
    try {
      await api.post(`/certificates/${revokeTarget.id}/revoke`, { reason: revokeReason.trim() });
      toast.success('Certificate revoked');
      setRevokeTarget(null);
      setRevokeReason('');
      fetchCerts();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to revoke');
    } finally {
      setRevoking(false);
    }
  };

  const filtered = certificates.filter(c => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return (
      c.user_name?.toLowerCase().includes(q) ||
      c.course_title?.toLowerCase().includes(q) ||
      c.certificate_number?.toLowerCase().includes(q)
    );
  });

  return (
    <DashboardLayout requiredRoles={['super_admin']}>
      <div className="space-y-6" data-testid="admin-certificates-page">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-[#2a3e5d]">Certificate Management</h1>
          <p className="text-slate-500 mt-1">View and manage all issued certificates</p>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[200px] max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              placeholder="Search by name, course, or cert number..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className="pl-9"
              data-testid="cert-search-input"
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-40" data-testid="cert-status-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Certificates</SelectItem>
              <SelectItem value="valid">Valid Only</SelectItem>
              <SelectItem value="revoked">Revoked Only</SelectItem>
            </SelectContent>
          </Select>
          <Badge className="bg-slate-100 text-slate-700">{filtered.length} certificate{filtered.length !== 1 ? 's' : ''}</Badge>
        </div>

        {/* Table */}
        {loading ? (
          <div className="flex justify-center py-20">
            <Loader2 className="h-8 w-8 animate-spin text-[#20acf6]" />
          </div>
        ) : filtered.length === 0 ? (
          <Card className="border-slate-200">
            <CardContent className="py-16 text-center">
              <Award className="h-12 w-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500">No certificates found</p>
            </CardContent>
          </Card>
        ) : (
          <Card className="border-slate-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="admin-cert-table">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200">
                    <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Certificate</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Holder</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Course</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Issued</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Status</th>
                    <th className="text-right py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(cert => (
                    <tr key={cert.id} className="border-b border-slate-100 hover:bg-slate-50/50" data-testid={`admin-cert-row-${cert.id}`}>
                      <td className="py-3 px-4">
                        <span className="font-mono text-xs text-slate-700">{cert.certificate_number}</span>
                      </td>
                      <td className="py-3 px-4">
                        <span className="font-medium text-slate-800">{cert.user_name}</span>
                      </td>
                      <td className="py-3 px-4">
                        <span className="text-slate-700 line-clamp-1">{cert.course_title}</span>
                      </td>
                      <td className="py-3 px-4 text-slate-500 whitespace-nowrap">
                        {new Date(cert.completion_date || cert.created_at).toLocaleDateString('en-GB')}
                      </td>
                      <td className="py-3 px-4">
                        {cert.is_valid ? (
                          <Badge className="bg-green-100 text-green-700 text-xs">
                            <ShieldCheck className="h-3 w-3 mr-1" /> Valid
                          </Badge>
                        ) : (
                          <Badge className="bg-red-100 text-red-700 text-xs">
                            <ShieldX className="h-3 w-3 mr-1" /> Revoked
                          </Badge>
                        )}
                      </td>
                      <td className="py-3 px-4 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            size="sm" variant="outline" className="text-xs h-7"
                            onClick={() => window.open(`/verify/${cert.certificate_number}`, '_blank')}
                            data-testid={`admin-verify-${cert.id}`}
                          >
                            <ExternalLink className="h-3 w-3 mr-1" /> View
                          </Button>
                          {cert.is_valid && (
                            <Button
                              size="sm" variant="outline"
                              className="text-xs h-7 text-red-600 border-red-200 hover:bg-red-50"
                              onClick={() => { setRevokeTarget(cert); setRevokeReason(''); }}
                              data-testid={`admin-revoke-btn-${cert.id}`}
                            >
                              <Ban className="h-3 w-3 mr-1" /> Revoke
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        )}
      </div>

      {/* Revoke Dialog */}
      <Dialog open={!!revokeTarget} onOpenChange={() => setRevokeTarget(null)}>
        <DialogContent data-testid="revoke-dialog">
          <DialogHeader>
            <DialogTitle className="text-red-600 flex items-center gap-2">
              <Ban className="h-5 w-5" /> Revoke Certificate
            </DialogTitle>
            <DialogDescription>
              Revoking certificate <strong className="font-mono">{revokeTarget?.certificate_number}</strong> for{' '}
              <strong>{revokeTarget?.user_name}</strong>. This action will mark the certificate as invalid on the public verification page.
            </DialogDescription>
          </DialogHeader>
          <div className="py-2">
            <label className="text-sm font-medium text-slate-700 mb-1.5 block">Reason for revocation *</label>
            <Select value={revokeReason} onValueChange={setRevokeReason}>
              <SelectTrigger data-testid="revoke-reason-select">
                <SelectValue placeholder="Select a reason..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Course fraud">Course fraud</SelectItem>
                <SelectItem value="Admin error">Admin error</SelectItem>
                <SelectItem value="Identity mismatch">Identity mismatch</SelectItem>
                <SelectItem value="Course content invalidated">Course content invalidated</SelectItem>
                <SelectItem value="Other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRevokeTarget(null)}>Cancel</Button>
            <Button
              className="bg-red-600 hover:bg-red-700 text-white"
              onClick={handleRevoke}
              disabled={!revokeReason.trim() || revoking}
              data-testid="confirm-revoke-btn"
            >
              {revoking ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Ban className="h-4 w-4 mr-1" />}
              Revoke Certificate
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
};

export default AdminCertificates;
